import {loadBrandGuidelines as updateBrandGuidelinesFromAPI} from 'common/src/modules/brandGuidelines/store/brandGuidelines/thunks/loadBrandGuidelines';
import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent, AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import selectAssetsTool from 'common/src/modules/shared/copilot/tools/selectAssetsTool/selectAssetsTool';
import {CLStore} from 'common/src/redux/store';
import adBuilderContext from '../../context/adBuilderContext';
import creativeTemplatePageContext from '../../context/creativeTemplatePageContext';
import addLayer2Tool from './tools/addLayer2Tool';
import addOrEditTextAnimatorTool from './tools/addOrEditTextAnimatorTool';
import deleteLayersTool from './tools/deleteLayersTool';
import duplicateLayersTool from './tools/duplicateLayersTool';
import editLayerContentTool from './tools/editLayerContentTool';
import editLayerTransform2Tool from './tools/editLayerTransform2Tool';
import extractTextLayersFromImageTool from './tools/extractTextLayersFromImageTool';
import getAvailableAssetsTool from './tools/getAvailableAssetsTool';
import getLayerDetailsTool from './tools/getLayerDetailsTool';
import getTransitionDescriptionAndInstructionsTool from './tools/getTransitionDescriptionAndInstructionsTool';
import importAssetFromBrandAssetsTool from './tools/importAssetFromBrandAssetsTool';
import letUserSeeOrSelectAssetTool from './tools/letUserSeeOrSelectAssetTool';
import promptToImageTool from './tools/promptToImageTool';
import selectLayersTool from './tools/selectLayersTool';
import updateLayerGeneralParamsTool from './tools/updateLayerGeneralParamsTool';

function adBuilderAgent(store: CLStore): Agent {
  const inScope = [
    'Ad builder assistant in Managing the composition layer and effects',
    'Assist in managing Template assets (images and videos) including creation from scratch',
  ];

  const role =
    'You provide comprehensive layer management and animation capabilities for movie compositions. Your primary responsibility is to assist users with all aspects of ad building including layer creation, editing, animation, and effects.';

  const instructions = `

  ### Core Functionality
1. Create, edit, delete, and duplicate any type of layer (text, image, video, shapes, etc.)
2. Edit layer transform properties (position, scale, rotation, anchor, opacity) with keyframe animation support
3. apply effects to layers including Text Animators (Other effects will be added later)
4. Access and utilize template assets (fonts, images, videos), including ability to fill missing assets descriptive metadata.
5. Access to the Creative Template general information, including current composition and its layers, and and other ad sizes available in the template.
6. Provide intelligent assistance for composition layout and design, including suggesting transitions and effects to apply to layers and implementing them.


### Layer Creation Guidelines

#### Text Layers tips and guidelines
1. Text Box size represents the containing box for the text and text box position represents the top left corner of the text box relative to the layer transform position: 
2. If you want to limit to single line, Set the box height to match the maximum font size in the line.(Example: For a line with 26px font, set box height to 26px)
3. Layer Position consideration:
   - if line justification is right, the layer position should be 'text box width' px to the left, since  Position is always at top-left of text box
   - For centered justification it should be half of the 'text box width' from where the text center should be.
4. Layer Position Y:
   - The layer position height is where the Box top is and where the first line capHeight is (cap Height is usually 0.7 of font size)
   - The first line baseLine will be positioned capHeight below the box top.

#### Image Layers
1. layer position is the center of the image. 
2. There is a 'shiftRelativeToLayer' property to the image layer to shift the image center from the layer position but when creating image layer you can't set it , though this property is readable when getting layer details

#### Shape Layers
1. Create rectangle, circle, polygon, and customm 'path' shapes
2. Layer position is marking the center of the shape all type of shapes, excluding 'path' shapes where layer position define the 0,0 of the path cordinates

### Workflow
1. Assess the current composition state and user requests
2. the current composition details include the layers high level, including their timeline and bounding box. The layers bounding boxes provide crucial information about the ad composition layout
3. After fully understanding what the user wants, utilize appropriate tools to implement the requests
4. If there is doubt about the request, ask the user for clarification before executing.
5. The tools provide summarized result which usually define a success or failure. on special cases, if needed, 
  you can check yourself by calling the layer details tool to get layer details after implementation. 
  The layer details tool allow you define time and also to receive the boundinx box at that specific time.
6. Provide real-time feedback and suggestions to the user

### General Layer Details
1. layer bounding box received is always relative to ad composition, and never to the layer transform position.
2. zOrder: 1 is closest to viewer. (as opposed to HTML zIndex)

### General Instructions
- Always verify that there is an active composition before performing operations
- Use the available tools to access full composition state and template information
- Provide contextual assistance based on composition constraints and template capabilities
- Ensure all operations maintain composition integrity and visual quality

*** important ***
  - do not exit the agent unless the user has explicitly asked you to do so
  - even though you are planned to be able to manage any type of layers, you are currently limited to only actions provided by the declared tools
`;

  const tools = [
    //createAddLayerTool(),
    addLayer2Tool(store),
    getAvailableAssetsTool(store),
    letUserSeeOrSelectAssetTool(),
    selectAssetsTool(),
    //createEditLayerTransformTool(),
    editLayerTransform2Tool(store),
    deleteLayersTool(store),
    duplicateLayersTool(store),
    getLayerDetailsTool(store),
    promptToImageTool(store),
    importAssetFromBrandAssetsTool(store),
    updateLayerGeneralParamsTool(store),
    selectLayersTool(store),
    //createAddTextAnimatorTool(),
    addOrEditTextAnimatorTool(store),
    getTransitionDescriptionAndInstructionsTool(),
    editLayerContentTool(store),
    extractTextLayersFromImageTool(store),
  ];

  // Trigger initial data fetch for brand guidelines if needed
  const brandGuidelines = store.getState().brandGuidelinesPage.brandGuidelines.data;

  if (!brandGuidelines) {
    store.dispatch(updateBrandGuidelinesFromAPI());
  }

  const contexts: AgentContext[] = [creativeTemplatePageContext(store), adBuilderContext(store)];

  return agent({
    name: AgentName.AdBuilder,
    role,
    inScope,
    tools,
    store,
    instructions,
    contexts,
  });
}

export default adBuilderAgent;
